import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as LinkIcon } from '@pkg/assets/icons/v2/link-alt.svg';
import { ReactComponent as ShareIcon } from '@pkg/assets/icons/v2/share.svg';
import { CopyModule, YatSprite } from '@pkg/components';
import OpenYat from '@pkg/components/Elements/OpenYat/OpenYat';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { Sizes } from '@pkg/components/Sections/Profile/Buttons/Button/types';
import { Colors, Variants } from '@pkg/components/Sections/Profile/Buttons/ButtonBase/types';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import { setMobileShareModalIsOpen } from '@app/store/actions/dashboard';

import { ShareModuleWrapper, YatButtonsWrapper, YatWrapper } from './styles';

interface Props {
    emojiId: EmojiId;
    className?: string;
    hideShare?: boolean;
    soloButton?: boolean;
    directionUp?: boolean;
    directionDown?: boolean;
    directionRight?: boolean;
    directionLeft?: boolean;
    positionCenter?: boolean;
    buttonColor?: string;
    buttonVariant?: string;
    buttonSize?: string;
    customButton?: React.ReactNode;
}

export const YatShareModule = ({
    emojiId,
    className,
    hideShare = false,
    soloButton = false,
    directionUp = false,
    directionDown = false,
    directionRight = false,
    directionLeft = false,
    buttonColor = 'translucent',
    buttonVariant = 'solid',
    buttonSize = 'small',
    customButton,
}: Props) => {
    const { t } = useTranslation(['translation', 'copy_widget', 'words']);
    const { mobileShareModalIsOpen } = useAppSelector((state) => state.dashboard);
    const { mobile } = useMediaQuery();

    const dispatch = useAppDispatch();

    const openMobileShare = useCallback(() => {
        dispatch(setMobileShareModalIsOpen(true));
    }, [dispatch]);

    if (customButton) {
        return (
            <>
                {!mobile && (
                    <CopyModule
                        iconSize={'default'}
                        emojiId={emojiId}
                        directionUp={directionUp}
                        directionDown={directionDown}
                        directionRight={directionRight}
                        directionLeft={directionLeft}
                    >
                        {customButton}
                    </CopyModule>
                )}

                {mobile && (
                    <Button
                        variant={Variants['highlight']}
                        color={Colors[buttonColor]}
                        size={Sizes[buttonSize]}
                        startIcon={<ShareIcon />}
                        style={{ textTransform: 'capitalize' }}
                        onClick={openMobileShare}
                    >
                        {t('words:share')}
                    </Button>
                )}
            </>
        );
    }

    if (soloButton) {
        return (
            <>
                {!mobile && (
                    <CopyModule
                        iconSize={'default'}
                        emojiId={emojiId}
                        directionUp={directionUp}
                        directionRight={directionRight}
                        directionLeft={directionLeft}
                    >
                        <Button
                            variant={Variants[buttonVariant]}
                            color={Colors[buttonColor]}
                            size={Sizes[buttonSize]}
                            startIcon={<ShareIcon />}
                            style={{ textTransform: 'capitalize' }}
                        >
                            {t('words:share')}
                        </Button>
                    </CopyModule>
                )}

                {mobile && (
                    <Button
                        variant={Variants[buttonVariant]}
                        color={Colors[buttonColor]}
                        size={Sizes[buttonSize]}
                        startIcon={<ShareIcon />}
                        style={{ textTransform: 'capitalize' }}
                        onClick={openMobileShare}
                    >
                        {t('words:share')}
                    </Button>
                )}
            </>
        );
    }

    return (
        <ShareModuleWrapper className={className}>
            <YatWrapper>
                {t('translation:yat_url')}/
                <YatSprite forceSprite={true} emojiID={emojiId} />
            </YatWrapper>
            {!hideShare && mobile && (
                <YatButtonsWrapper onClick={openMobileShare}>
                    <LinkIcon
                        className="copy-module__icon-button"
                        aria-label={t('copy_widget:alt_copy_link_icon')}
                        aria-expanded={mobileShareModalIsOpen}
                    />
                </YatButtonsWrapper>
            )}
            {!hideShare && !mobile && (
                <YatButtonsWrapper>
                    <CopyModule iconSize={'default'} emojiId={emojiId} />
                </YatButtonsWrapper>
            )}
            <YatButtonsWrapper>
                <OpenYat emojiId={emojiId} iconSize={'default'} />
            </YatButtonsWrapper>
        </ShareModuleWrapper>
    );
};
